import {
  AgGrid,
  Button,
  CommonLink,
  Datepicker,
  ErrorMessage,
  FileInput,
  Heading,
  InfoPopup,
  Spinner,
  SuccessPopup,
  MobileResponsiveGrid,
} from "@/common/components";
import { useContext, useEffect, useState } from "@/common/hooks";
import { PrivateContext } from "@/routes/PrivateRoute";
import {
  useGetPaymentTableAuditMutation,
  useGetPaymentTableMutation,
  useGetProductInsuranceCompaniesQuery,
  useGetWithdrawalTableMutation,
  useUpdatePaymentTableMutation,
  useUpdateWithdrawalTableMutation,
} from "@/service/ProductService";
import { Constants } from "@/utils/Constants";
import {
  formatDecimalField,
  getFormattedDateTime,
  getProductOptionValue,
  scrollToTop,
  validateAge,
  validatePrice,
} from "@/utils/Utilities";
import Papa from "papaparse";

const PaymentTable = () => {
  const {
    labels,
    setNavHeader,
    setPageHeader,
    location,
    cloneDeep,
    cancel,
    accessorPrivileges,
    isRiccAdministrator,
  } = useContext(PrivateContext);

  const [searchDate, setSearchDate] = useState("");
  const [newTableEffectiveDate, setNewTableEffectiveDate] = useState("");
  const [rowData, setRowData] = useState([]);
  const [historyData, setHistoryData] = useState([]);
  const [attachment, setAttachment] = useState("");
  const [showImportPopup, setShowImportPopup] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [errors, setErrors] = useState(null);
  const [csvErrors, setCsvErrors] = useState(null);
  const [showHistoryGrid, setShowHistoryGrid] = useState(false);
  const [productTypeStandardOrWeighted, setProductTypeStandardOrWeighted] =
    useState(false);
  const [fixedProduct, setFixedProduct] = useState(false);
  const [gmibProduct, setGmibProduct] = useState(false);
  const [maxResults, setMaxResults] = useState(100);
  const [searchClicked, setSearchClicked] = useState(false);
  const [showNoPaymentTable, setShowNoPaymentTable] = useState(false);

  const columnDefs = [
    {
      headerName:
        fixedProduct || gmibProduct
          ? labels?.paymentTable?.age
          : labels?.paymentTable?.ownerAge,
      field: "ageYearCount",
    },
    // For Fixed Product
    {
      headerName: labels?.paymentTable?.singleFactor,
      field: "fixedSharePrice",
      editable: true,
      hide: !fixedProduct,
      valueParser: (p) => formatDecimalField(p, 0.0, 99999.9999999999, 10),
      valueFormatter: (p) => p?.value ?? "",
      inputMode: "decimal",
      onChange: ({ next, data, field }) => {
        setRowData((prev) =>
          prev.map((r) =>
            String(r.ageYearCount) === String(data.ageYearCount)
              ? { ...r, [field]: next, modified: true }
              : r
          )
        );
      },
    },
    // For gmwbCalculationMethod W or S
    {
      headerName: labels?.paymentTable?.singleFactor,
      field: "variableSingleOwnerPercentageRate",
      editable: true,
      hide: fixedProduct || !productTypeStandardOrWeighted,
      valueParser: (p) => formatDecimalField(p, 0.0, 99999.9999999999, 10),
      valueFormatter: (p) => p?.value ?? "",
      inputMode: "decimal",
      onChange: ({ next, data, field }) => {
        setRowData((prev) =>
          prev.map((r) =>
            String(r.ageYearCount) === String(data.ageYearCount)
              ? { ...r, [field]: next, modified: true }
              : r
          )
        );
      },
    },
    {
      headerName: labels?.paymentTable?.jointFactor,
      field: "variableJointOwnerPercentageRate",
      editable: true,
      hide: fixedProduct || !productTypeStandardOrWeighted,
      valueParser: (p) => formatDecimalField(p, 0.0, 99999.9999999999, 10),
      valueFormatter: (p) => p?.value ?? "",
      inputMode: "decimal",
      onChange: ({ next, data, field }) => {
        setRowData((prev) =>
          prev.map((r) =>
            String(r.ageYearCount) === String(data.ageYearCount)
              ? { ...r, [field]: next, modified: true }
              : r
          )
        );
      },
    },
    // For gmwbCalculationMethod T
    {
      headerName: labels?.paymentTable?.cRate,
      field: "cTypePercentageRate",
      editable: true,
      hide: fixedProduct || gmibProduct || productTypeStandardOrWeighted,
      valueParser: (p) => formatDecimalField(p, 0.0, 99999.9999999999, 10),
      valueFormatter: (p) => p?.value ?? "",
      inputMode: "decimal",
      onChange: ({ next, data, field }) => {
        setRowData((prev) =>
          prev.map((r) =>
            String(r.ageYearCount) === String(data.ageYearCount)
              ? { ...r, [field]: next, modified: true }
              : r
          )
        );
      },
    },
    {
      headerName: labels?.paymentTable?.tRate,
      field: "tTypePercentageRate",
      editable: true,
      hide: fixedProduct || gmibProduct || productTypeStandardOrWeighted,
      valueParser: (p) => formatDecimalField(p, 0.0, 99999.9999999999, 10),
      valueFormatter: (p) => p?.value ?? "",
      inputMode: "decimal",
      onChange: ({ next, data, field }) => {
        setRowData((prev) =>
          prev.map((r) =>
            String(r.ageYearCount) === String(data.ageYearCount)
              ? { ...r, [field]: next, modified: true }
              : r
          )
        );
      },
    },
  ];

  const historyDefs = [
    {
      headerName: labels?.paymentTable?.date,
      field: "lastMntDateTime",
      cellRenderer: getFormattedDateTime,
      flex: 2,
    },
    {
      headerName: labels?.paymentTable?.age,
      field: "ageYearCount",
    },
    {
      headerName: labels?.paymentTable?.singleFactor,
      field: "variableSingleOwnerPercentageRate",
      hide: fixedProduct || !productTypeStandardOrWeighted,
      valueFormatter: (params) =>
        formatDecimalField(params, 0.0, 99999.9999999999, 4),
    },
    {
      headerName: labels?.paymentTable?.singleFactor,
      field: "fixedSharePrice",
      hide: !fixedProduct,
      valueFormatter: (params) =>
        formatDecimalField(params, 0.0, 99999.9999999999, 2),
    },
    {
      headerName: labels?.paymentTable?.jointFactor,
      field: "variableJointOwnerPercentageRate",
      hide: fixedProduct || !productTypeStandardOrWeighted,
      valueFormatter: (params) =>
        formatDecimalField(params, 0.0, 99999.9999999999, 4),
    },
    {
      headerName: labels?.paymentTable?.operatorId,
      field: "lastMntOpId",
    },
  ];

  const [getPaymentTableApi, { isLoading: isFetching, error }] =
    useGetPaymentTableMutation();

  const {
    data: insuranceCompanies,
    isFetching: isFetching1,
    error: error1,
  } = useGetProductInsuranceCompaniesQuery({
    productSponsorId: location?.state?.productSponsorId,
    productId: location?.state?.productId,
  });

  const [updatePaymentTableApi, { isLoading: isFetching2, error: error2 }] =
    useUpdatePaymentTableMutation();

  const [getWithdrawalTableApi, { isLoading: isFetching3, error: error3 }] =
    useGetWithdrawalTableMutation();

  const [updateWithdrawalTableApi, { isLoading: isFetching4, error: error4 }] =
    useUpdateWithdrawalTableMutation();

  const [getPaymentTableAuditApi, { isLoading: isFetching5, error: error5 }] =
    useGetPaymentTableAuditMutation();

  const getPayload = (newDate) => {
    return {
      productSponsorId: location?.state?.productSponsorId,
      productId: location?.state?.productId,
      insuranceCompanyId:
        insuranceCompanies?.productInsurers[0]?.insuranceCompanyId,
      insuranceCompanyEffectiveDate:
        productTypeStandardOrWeighted && !(fixedProduct || gmibProduct)
          ? null
          : insuranceCompanies?.productInsurers[0]?.effectiveDate,
      tableEffectiveDate: newDate || searchDate,
    };
  };

  const doSearch = (newDate) => {
    setAttachment([]);
    setShowNoPaymentTable(false);
    setErrors(null);

    const payload = getPayload(newDate);

    const api =
      productTypeStandardOrWeighted || fixedProduct || gmibProduct
        ? getPaymentTableApi(payload)
        : getWithdrawalTableApi(payload);

    api.unwrap().then((res) => {
      if (newDate === res?.insurerPaymentTableAges[0]?.tableEffectiveDate) {
        setErrors({
          errorMessage: labels?.paymentTable?.alreadyExists,
        });
        scrollToTop();
      } else {
        if (newDate) {
          setShowImportPopup(true);
        }
      }
      const data = cloneDeep(res?.insurerPaymentTableAges || []);

      if (!(fixedProduct || gmibProduct)) {
        let firstItem = productTypeStandardOrWeighted
          ? data[0]?.variableJointOwnerPercentageRate
          : data[0]?.cTypePercentageRate;

        if (!firstItem) {
          setShowNoPaymentTable(true);
          return;
        }
      }

      setRowData(data);
    });
  };

  const getAuditData = () => {
    const payload = getPayload(false);

    payload.firstResult = 0;
    payload.maxResults = maxResults;

    getPaymentTableAuditApi(payload)
      .unwrap()
      .then((res) => {
        if (res?.moreRecords === "true") {
          setMaxResults(maxResults + 100);
        } else {
          setMaxResults(0);
        }
        const data = cloneDeep(res?.insurerPaymentTableAgeAudits).sort(
          (a, b) => {
            return new Date(b.lastMntDateTime) - new Date(a.lastMntDateTime);
          }
        );
        setHistoryData(data);
      });
  };

  const importCsvDataFromPapa = (parsedRows, minAge, maxAge) => {
    const paymentRateTables = [];

    parsedRows.forEach((row, index) => {
      if (!row || Object.values(row).every((val) => val.trim?.() === ""))
        return;

      validateAge(row[0]?.trim(), index, Constants.AGE_COLUMN);
      validatePrice(row[1]?.trim(), index, Constants.SINGLE_PRICE_COLUMN);
      validatePrice(row[2]?.trim(), index, Constants.JOINT_PRICE_COLUMN);

      const paymentTable = {
        ageYearCount: parseInt(row[0]?.trim()).toString(),

        // Single and Joint will be for S & W
        variableSingleOwnerPercentageRate: productTypeStandardOrWeighted
          ? parseFloat(row[1]?.trim()).toFixed(10).toString()
          : null,
        variableJointOwnerPercentageRate: productTypeStandardOrWeighted
          ? parseFloat(row[2]?.trim()).toFixed(10).toString()
          : null,

        // cType and tType will be for T
        cTypePercentageRate: !productTypeStandardOrWeighted
          ? parseFloat(row[1]?.trim()).toFixed(10).toString()
          : null,
        tTypePercentageRate: !productTypeStandardOrWeighted
          ? parseFloat(row[2]?.trim()).toFixed(10).toString()
          : null,

        fixedSharePrice: null,
        productSponsorId: location?.state?.productSponsorId,
        productId: location?.state?.productId,
        insuranceCompanyId:
          insuranceCompanies?.productInsurers[0]?.insuranceCompanyId,
        effectiveDate: insuranceCompanies?.productInsurers[0]?.effectiveDate,
        tableEffectiveDate: newTableEffectiveDate,
      };

      paymentRateTables.push(paymentTable);
    });

    if (!paymentRateTables.length) {
      throw new Error("No data to import.");
    }

    paymentRateTables.sort(
      (a, b) => parseInt(a.ageYearCount) - parseInt(b.ageYearCount)
    );

    const expectedCount = maxAge - minAge + 1;
    const actualCount = paymentRateTables.length;

    if (expectedCount !== actualCount) {
      throw new Error(
        "Data range didn't match the defined min/max age values."
      );
    }

    const firstAge = parseInt(paymentRateTables[0].ageYearCount);
    const lastAge = parseInt(
      paymentRateTables[paymentRateTables.length - 1].ageYearCount
    );

    if (firstAge !== minAge) {
      throw new Error("Minimum age didn't match the defined min age value.");
    }

    if (lastAge !== maxAge) {
      throw new Error("Maximum age didn't match the defined max age value.");
    }

    return paymentRateTables;
  };

  const importCsv = () => {
    setCsvErrors(null);

    const file = attachment[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setCsvErrors({
        errorMessage: labels?.paymentTable?.invalidFileError,
      });
      return;
    }

    const minAge = getProductOptionValue(
      location,
      "PRODUCT_MIN_PAYMENT_TABLE_AGE_RANGE"
    );
    const maxAge = getProductOptionValue(
      location,
      "PRODUCT_MAX_PAYMENT_TABLE_AGE_RANGE"
    );

    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (result) => {
        try {
          const data = importCsvDataFromPapa(
            result.data,
            parseInt(minAge),
            parseInt(maxAge)
          );
          setRowData(data);
        } catch (err) {
          setCsvErrors({
            errorMessage: `${labels?.paymentTable?.csvError}, ${err["message"]}`,
          });
        }
      },
    });
  };

  const updatePaymentTable = () => {
    const payload = {
      productSponsorId: location?.state?.productSponsorId,
      productId: location?.state?.productId,
      insuranceCompanyId:
        insuranceCompanies?.productInsurers[0]?.insuranceCompanyId,
      insuranceCompanyEffectiveDate:
        insuranceCompanies?.productInsurers[0]?.effectiveDate,
      insurerPaymentTableAges: rowData,
      tableEffectiveDate: newTableEffectiveDate
        ? newTableEffectiveDate
        : rowData[0]?.tableEffectiveDate,
    };

    if (fixedProduct || gmibProduct) {
      payload.tableEffectiveDate = null;
    }

    const api =
      productTypeStandardOrWeighted || fixedProduct || gmibProduct
        ? updatePaymentTableApi(payload)
        : updateWithdrawalTableApi(payload);

    api.unwrap().then((res) => {
      if (!res.inError) {
        setShowPopup(true);
      }
    });
  };

  const handlePaymentTableReset = () => {
    setSearchDate("");
    setNewTableEffectiveDate("");
    setRowData([]);
    setAttachment([]);
    setErrors(null);
    setCsvErrors(null);
    setShowImportPopup(false);
    setShowPopup(false);
    setShowNoPaymentTable(false);
    setSearchClicked(false);
  };

  useEffect(() => {
    if (insuranceCompanies) {
      const gmwbCalculationMethod = getProductOptionValue(
        location,
        "GMWB_CALCULATION_METHOD"
      );
      let productType = gmwbCalculationMethod === "T";

      setProductTypeStandardOrWeighted(!productType);
      setFixedProduct(location?.state?.productType === "F");
      setGmibProduct(location?.state?.productType === "I");
    }
  }, [location, insuranceCompanies]);

  useEffect(() => {
    if ((fixedProduct || gmibProduct) && insuranceCompanies?.productInsurers) {
      doSearch();
      getAuditData();
    }
  }, [fixedProduct, gmibProduct, insuranceCompanies]);

  useEffect(() => {
    importCsv();
    setShowNoPaymentTable(false);
  }, [attachment]);

  useEffect(() => {
    if (searchDate) {
      setNewTableEffectiveDate("");
    }
  }, [searchDate]);

  useEffect(() => {
    if (newTableEffectiveDate) {
      setRowData([]);
      setSearchDate("");
    }
  }, [newTableEffectiveDate]);

  useEffect(() => {
    setPageHeader(
      labels?.paymentTable?.paymentTableFor + location?.state?.name
    );
    setNavHeader(labels?.navbar?.product);
  }, []);

  const hasAccessToUpdatePaymentTable = () => {
    if (isRiccAdministrator) {
      return true;
    } else {
      return (
        accessorPrivileges["INSURANCE PURCHASE / PAYMENT TABLES"] == "ADD" ||
        accessorPrivileges["INSURANCE PURCHASE / PAYMENT TABLES"] == "UPDATE"
      );
    }
  };

  return (
    <section className="position-relative">
      {(isFetching ||
        isFetching1 ||
        isFetching2 ||
        isFetching3 ||
        isFetching4 ||
        isFetching5) && <Spinner />}
      <section className="container-medium py-5">
        <ErrorMessage
          error={
            errors || error || error1 || error2 || error3 || error4 || error5
          }
          wrapperClassName="mb-3"
        />
        {!(fixedProduct || gmibProduct) && (
          <>
            <Heading
              text={labels?.paymentTable?.paymentTableSearch}
              wrapperClassNames="h4 border-bottom-1 pb-2 mb-3"
              title={false}
            />
            <div className="mb-4">
              <Datepicker
                id="searchDate"
                name="searchDate"
                wrapperClassName="form-group col-md-5"
                label={labels?.paymentTable?.searchDate}
                value={searchDate}
                max={new Date()}
                change={(e) => {
                  setSearchDate(e);
                }}
              />

              <div className="d-flex flex-md-row flex-row-reverse gap-1 mb-4">
                <Button
                  id="search"
                  text={labels?.paymentTable?.search}
                  wrapperClassName="col-md-3 col-6"
                  click={() => {
                    setSearchClicked(!searchClicked);
                    doSearch();
                  }}
                  disabled={!searchDate}
                />
                <Button
                  id="reset"
                  text={labels?.paymentTable?.reset}
                  wrapperClassName="col-md-3 col-6"
                  click={handlePaymentTableReset}
                  type="secondary"
                  disabled={!searchDate}
                />
              </div>
            </div>
            {hasAccessToUpdatePaymentTable() && (
              <Datepicker
                key={searchClicked}
                id="effectiveDate"
                name="effectiveDate"
                wrapperClassName="form-group col-md-5 mb-4"
                label={labels?.paymentTable?.newTableEffectiveDate}
                value={newTableEffectiveDate}
                max={new Date()}
                change={(e) => {
                  setNewTableEffectiveDate(e);
                }}
              />
            )}
            <Heading
              text={labels?.paymentTable?.paymentTable}
              wrapperClassNames="h4 border-bottom-1 pb-2 mb-3"
              title={false}
            />
            {hasAccessToUpdatePaymentTable() && (
              <CommonLink
                wrapperClassName="mb-3"
                disabled={!newTableEffectiveDate}
              >
                <span onClick={() => doSearch(newTableEffectiveDate)}>
                  {labels?.paymentTable?.importPaymentTable}
                </span>
              </CommonLink>
            )}
            {showImportPopup && (
              <div className="mb-3">
                <InfoPopup
                  heading={labels?.paymentTable?.paymentRateTable}
                  showModal={showImportPopup}
                  primaryButtonText="Import"
                  primaryButtonClick={() => {
                    setShowImportPopup(false);
                  }}
                  primaryButtonDisabled={
                    !attachment?.length || csvErrors?.errorMessage
                  }
                  secondaryButtonText="Cancel"
                  secondaryButtonClick={() => {
                    setAttachment([]);
                    setCsvErrors(null);
                    setShowImportPopup(false);
                    setRowData([]);
                  }}
                  body={
                    <>
                      <FileInput
                        value={attachment}
                        accept=".csv"
                        id="file-attachment"
                        onChange={(e) => {
                          setAttachment(e);
                        }}
                      />
                      <ErrorMessage error={csvErrors} />
                    </>
                  }
                />
              </div>
            )}
          </>
        )}
        {showNoPaymentTable ? (
          <div className="grid-detail-box py-4 mb-4 d-flex justify-content-center">
            <div className="primary-color small">
              {labels?.paymentTable?.noPaymentTable}
            </div>
          </div>
        ) : !showImportPopup && rowData.length > 0 ? (
          <>
            {!(fixedProduct || gmibProduct) && searchDate && (
              <div className="mb-2">
                {labels?.paymentTable?.tableEffectiveDate}
                {!productTypeStandardOrWeighted
                  ? insuranceCompanies?.productInsurers[0]?.effectiveDate
                  : rowData[0].tableEffectiveDate}
              </div>
            )}
            <AgGrid
              wrapperClassNames="mb-3 "
              columnDefs={columnDefs}
              rowData={rowData}
              domLayout={"normal"}
              mainClassNames="d-block"
              defaultColDef={{
                sortable: true,
              }}
            />
          </>
        ) : (
          <div className="grid-detail-box py-4 mb-4 d-flex justify-content-center">
            <div className="primary-color small">
              {hasAccessToUpdatePaymentTable()
                ? labels?.paymentTable?.searchOrImportPaymentTable
                : labels?.paymentTable?.searchPaymentTable}
            </div>
          </div>
        )}
        {(fixedProduct || gmibProduct) && (
          <>
            <Heading
              text={labels?.paymentTable?.paymentTableHistory}
              title={false}
              wrapperClassNames="h4 my-4 border-bottom border-black pb-2"
            />
            <CommonLink wrapperClassName="grid-detail-box d-flex justify-content-center mb-2">
              <span onClick={() => setShowHistoryGrid(!showHistoryGrid)}>
                {`${
                  !showHistoryGrid
                    ? labels?.paymentTable?.show
                    : labels?.paymentTable?.hide
                } ${labels?.paymentTable?.auditHistory}`}
              </span>
            </CommonLink>
          </>
        )}
        {hasAccessToUpdatePaymentTable() && (
          <Button
            id="done"
            text={labels?.paymentTable?.done}
            disabled={!rowData.length || showNoPaymentTable}
            click={updatePaymentTable}
            wrapperClassName="col-md-4 mt-5 mx-auto"
          />
        )}
        <SuccessPopup
          showModal={showPopup}
          buttonText={labels?.paymentTable?.continue}
          click={cancel}
          body={labels?.paymentTable?.successMsg}
        />
      </section>

      {showHistoryGrid && (
        <AgGrid
          wrapperClassNames="pb-5 d-md-block d-none"
          columnDefs={historyDefs}
          rowData={historyData}
          domLayout="normal"
          overlayNoRowsTemplate={labels?.paymentTable?.noHistory}
          defaultColDef={{
            sortable: true,
          }}
        >
          {maxResults ? (
            <CommonLink wrapperClassName="d-flex justify-content-end mb-2">
              <span onClick={() => getAuditData()}>
                {labels?.paymentTable?.more}
              </span>
            </CommonLink>
          ) : (
            ""
          )}
        </AgGrid>
      )}

      {showHistoryGrid && (
        <div className="pb-3">
          <MobileResponsiveGrid
            rowData={historyData}
            columnDefs={historyDefs}
            showSortDropDown={false}
          />
        </div>
      )}
    </section>
  );
};

export default PaymentTable;
